import React, { useState, useEffect } from "react";
import { Location } from "history";
import { toast } from "react-toastify";
import { match as Match } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { injectIntl, IntlShape } from "react-intl";
import Switch, { SwitchChangeEventHandler } from "rc-switch";
import IntlMessages from "../../../../utils/messages";
import PageSubheader from "../../../../components/PageSubheader";
import Block from "../../../../components/Tailwind/Block";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import { withTwoColLayout } from "../../../../utils";
import { getTranslation } from "../../../../utils/translation";
import DropdownQuoteRequestStatus from "../../../../components/Tailwind/DropdownQuoteRequestStatus";
import overrideClasses from "../../../../utils/overrideClasses";
import {
  displayDayDDMMYYYY_HHMM,
  getCypressTestId,
} from "../../../../utils/config";
import QuoteRequestServiceSelections from "./selections";

interface Props {
  match: Match<{ cid: string; id: string }>;
  intl: IntlShape;
  backURL: string;
  location: Location;
}

const QuoteRequest = (props: Props) => {
  const [loading, setLoading] = useState(false);
  const [priority, setPriority] = useState<boolean>();
  const { intl, match, backURL, location } = props;

  const getQuoteRequest = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.getQuoteRequest
  );
  const quoteRequest = GroundGraphqlContextStore.useStoreState(
    state => state.quoteRequests.quoteRequest
  );
  const setQuotRequest = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.setQuotRequest
  );
  const listQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.listQuoteRequestStatus
  );
  const quoteRequestStatuss = GroundGraphqlContextStore.useStoreState(
    state => state.quoteRequests.quoteRequestStatuss.items
  );
  const updateQuoteRequest = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.updateQuoteRequest
  );

  const center = GroundGraphqlContextStore.useStoreState(
    state => state.center.center
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getQuoteRequest({ id: match.params.id }),
      listQuoteRequestStatus(),
    ]).finally(() => setLoading(false));

    return () => {
      setQuotRequest(null);
    };
  }, []);

  const onChangePriority: SwitchChangeEventHandler = checked => {
    setPriority(checked);

    if (quoteRequest) {
      updateQuoteRequest({
        id: quoteRequest.id,
        high_priority: checked,
      })
        .then(() => {
          toast(
            intl.formatMessage({
              id: "page.quote.requests.change.priority.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          toast(
            intl.formatMessage({
              id: "page.quote.requests.change.priority.error",
            }),
            {
              type: "error",
            }
          );
        });
    }
  };

  return !quoteRequest ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <>
        <Helmet>
          <title>
            {intl.formatMessage({
              id: "page.detail.quote.requests.document.title",
            })}
          </title>
        </Helmet>

        <PageSubheader
          goBackEnabled
          goBackId="btn-back-quote-request"
          goBackURL={backURL}
          location={location}
          title="page.detail.quote.requests.title"
        />

        <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
          <Block>
            <div className="py-3 sm:py-5 border-b border-gray-200 px-2 sm:px-6 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center">
                <div className="mt-1">
                  <div className="text-17px text-neutral-900 leading-5">
                    <IntlMessages
                      id="page.detail.quote.request.number"
                      values={{ ref: quoteRequest?.ref }}
                    />
                  </div>
                  {quoteRequest?.created_at && (
                    <div className="text-14px text-ground-gray-100 leading-relaxed">
                      {displayDayDDMMYYYY_HHMM(
                        quoteRequest.created_at,
                        centerTimezone
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-1 sm:w-1/6 justify-end">
                  {quoteRequest && quoteRequest.status && (
                    <div className="flex flex-row items-center space-x-2">
                      <DropdownQuoteRequestStatus
                        quoteRequest={quoteRequest}
                        quoteRequestStatus={quoteRequestStatuss.filter(s =>
                          [
                            ...quoteRequest.next_status,
                            quoteRequest.status.code,
                          ].includes(s.code)
                        )}
                        intl={intl}
                        className="items-start"
                        fromQuoteRequestDetails={true}
                        center={center}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="py-3 bg-gray-100 px-2 sm:px-6 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-start">
                <div className="flex flex-col text-14px leading-relaxed">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="general.client" />
                  </p>
                  <p className="text-ground-gray-100">
                    {quoteRequest?.user?.first_name}{" "}
                    {quoteRequest?.user?.last_name}
                  </p>
                  <p className="text-ground-blue-100">
                    {quoteRequest?.user?.email}
                  </p>
                </div>

                <div className="flex flex-col text-14px leading-relaxed">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="page.detail.quote.request.external_ref" />
                  </p>
                  <p className=" text-ground-gray-100">
                    {quoteRequest?.external_ref}
                  </p>
                </div>

                <div className="flex text-14px leading-relaxed space-x-2">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="page.detail.quote.request.priority" />
                  </p>
                  <Switch
                    id="switch_quote_request_priority"
                    data-cy="switch_quote_request_priority"
                    data-testid={getCypressTestId({
                      id: quoteRequest.id.toString(),
                    })}
                    disabled={quoteRequest.status.disabled}
                    checked={priority ?? !!quoteRequest.priority}
                    onChange={onChangePriority}
                  />
                </div>
              </div>
            </div>

            <div className="pt-3 pb-5 bg-gray-100 px-2 sm:px-6 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-start lg:w-1/2 sm:w-full">
                <div className="flex flex-col text-14px leading-relaxed">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="page.detail.quote.request.validator" />
                  </p>
                  <p className="text-ground-gray-100">
                    {quoteRequest?.back_office_user?.first_name}{" "}
                    {quoteRequest?.back_office_user?.last_name}
                  </p>
                  <p className="text-ground-blue-100">
                    {quoteRequest?.back_office_user?.email}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-3 pb-5 bg-gray-100 px-2 sm:px-6 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-start lg:w-1/3 sm:w-full">
                <div className="flex flex-col text-14px leading-relaxed">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="page.detail.quote.request.title" />
                  </p>
                  <p className=" text-ground-gray-100">
                    {getTranslation(JSON.stringify(quoteRequest?.title || {}))}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-3 pb-5 bg-gray-100 px-2 sm:px-6 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-start lg:w-1/2 sm:w-full">
                <div className="flex flex-col text-14px leading-relaxed">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="page.detail.quote.request.services" />
                  </p>
                  <p className=" text-ground-gray-100">
                    {quoteRequest?.services
                      .map(s => getTranslation(JSON.stringify(s.label)))
                      .join(", ")}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-3 pb-5 bg-gray-100 px-2 sm:px-6 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-start lg:w-1/3 sm:w-full">
                <div className="flex flex-col text-14px leading-relaxed">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="page.detail.quote.request.description" />
                  </p>
                  <p className=" text-ground-gray-100">
                    {getTranslation(JSON.stringify(quoteRequest?.description))}
                  </p>
                </div>
              </div>
            </div>
          </Block>

          <QuoteRequestServiceSelections
            selections={quoteRequest.selections}
            title="page.detail.quote.request.selections"
          />
        </div>
      </>
    </>
  );
};

export default withTwoColLayout(injectIntl(QuoteRequest));
